// Wizard Card Game Scoreboard
// Mobile-friendly interface for tracking bids, tricks, and scores
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button, Card } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import { getGame, updateGameStatus } from '../services/gameService';
import { saveGameResults } from '../services/resultService';
import {
  getWizardGame,
  createWizardGame,
  saveBidsForRound,
  saveTricksForRound,
  startNextRound,
  getDealerForRound,
  getStandings,
  validateTricks,
  resetWizardGame,
  calculateRoundScore,
  editRoundData
} from '../services/wizardGameService';

const WizardScoreboard = () => {
  const { competitionId, gameId } = useParams();
  const navigate = useNavigate();
  const { players, isOrganizer } = useCompetition();

  const [game, setGame] = useState(null);
  const [wizardGame, setWizardGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Current round input state
  const [bids, setBids] = useState({});
  const [tricks, setTricks] = useState({});

  // View state
  const [showHistory, setShowHistory] = useState(false);

  // Edit mode state
  const [editingRound, setEditingRound] = useState(null);
  const [editBids, setEditBids] = useState({});
  const [editTricks, setEditTricks] = useState({});
  const [editBidsOnly, setEditBidsOnly] = useState(false); // true = only editing bids (current round)

  // Game completion state
  const [showConfirmResults, setShowConfirmResults] = useState(false);

  useEffect(() => {
    if (players && players.length > 0) {
      loadData();
    }
  }, [gameId, players, isOrganizer]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const gameData = await getGame(gameId);
      if (!gameData) {
        setError('Game not found');
        setLoading(false);
        return;
      }
      setGame(gameData);

      let wizardData = await getWizardGame(gameId);

      if (!wizardData && isOrganizer && gameData.drawnOrder && gameData.drawnOrder.length >= 3) {
        const gamePlayers = players.filter(p => gameData.drawnOrder.includes(p.id));
        if (gamePlayers.length >= 3) {
          wizardData = await createWizardGame(gameId, gamePlayers, gameData.drawnOrder);
        }
      }

      if (wizardData) {
        setWizardGame(wizardData);
        initializeInputs(wizardData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading Wizard game:', err);
      setError('Failed to load game: ' + err.message);
      setLoading(false);
    }
  };

  const initializeInputs = (wizData) => {
    const currentRound = wizData.currentRound;
    const newBids = {};
    const newTricks = {};

    wizData.playerOrder.forEach(playerId => {
      const roundData = wizData.playerData[playerId]?.rounds[currentRound] || {};
      newBids[playerId] = roundData.bid !== undefined ? roundData.bid : '';
      newTricks[playerId] = roundData.tricks !== undefined ? roundData.tricks : '';
    });

    setBids(newBids);
    setTricks(newTricks);
  };

  const currentDealer = useMemo(() => {
    if (!wizardGame) return null;
    return getDealerForRound(wizardGame.playerOrder, wizardGame.currentRound);
  }, [wizardGame]);

  // Calculate made/busted stats for each player
  const standingsWithStats = useMemo(() => {
    if (!wizardGame) return [];
    const baseStandings = getStandings(wizardGame);

    return baseStandings.map(player => {
      const playerData = wizardGame.playerData[player.playerId];
      let made = 0;
      let busted = 0;

      if (playerData?.rounds) {
        Object.values(playerData.rounds).forEach(round => {
          if (round.score !== undefined) {
            if (round.bid === round.tricks) {
              made++;
            } else {
              busted++;
            }
          }
        });
      }

      return { ...player, made, busted };
    });
  }, [wizardGame]);

  const playersInOrder = useMemo(() => {
    if (!wizardGame) return [];
    return wizardGame.playerOrder.map(playerId => {
      const player = players.find(p => p.id === playerId);
      const data = wizardGame.playerData[playerId];
      return {
        id: playerId,
        displayName: data?.name || player?.displayName || 'Unknown',
        seatNumber: data?.seatNumber || 0,
        totalScore: data?.totalScore || 0,
        avatar: player?.avatar
      };
    });
  }, [wizardGame, players]);

  // Players in bidding order - first player after dealer bids first, dealer bids last
  const playersInBiddingOrder = useMemo(() => {
    if (!wizardGame || !currentDealer) return playersInOrder;
    // Find dealer index in seat order (0-indexed)
    const dealerSeatIndex = currentDealer.seatNumber - 1;
    const numPlayers = playersInOrder.length;

    // Build array starting from player after dealer
    const orderedPlayers = [];
    for (let i = 1; i <= numPlayers; i++) {
      const seatIndex = (dealerSeatIndex + i) % numPlayers;
      orderedPlayers.push(playersInOrder[seatIndex]);
    }
    return orderedPlayers;
  }, [playersInOrder, currentDealer, wizardGame]);

  const totalBids = useMemo(() => {
    return Object.values(bids).reduce((sum, b) => sum + (parseInt(b) || 0), 0);
  }, [bids]);

  const totalTricks = useMemo(() => {
    return Object.values(tricks).reduce((sum, t) => sum + (parseInt(t) || 0), 0);
  }, [tricks]);

  const allBidsEntered = useMemo(() => {
    if (!wizardGame) return false;
    return wizardGame.playerOrder.every(pid => bids[pid] !== '' && bids[pid] !== undefined);
  }, [wizardGame, bids]);

  const allTricksEntered = useMemo(() => {
    if (!wizardGame) return false;
    return wizardGame.playerOrder.every(pid => tricks[pid] !== '' && tricks[pid] !== undefined);
  }, [wizardGame, tricks]);

  // Bids cannot equal cards dealt - someone must miss
  const bidsAreValid = useMemo(() => {
    if (!wizardGame) return false;
    return allBidsEntered && totalBids !== wizardGame.currentRound;
  }, [wizardGame, allBidsEntered, totalBids]);

  // Calculate graph data: cumulative scores and positions per round
  const graphData = useMemo(() => {
    if (!wizardGame || !wizardGame.playerData) return null;

    const playerIds = wizardGame.playerOrder;
    const completedRounds = [];

    // Find how many rounds have been completed
    for (let r = 1; r <= wizardGame.totalRounds; r++) {
      const hasData = playerIds.some(pid =>
        wizardGame.playerData[pid]?.rounds[r]?.score !== undefined
      );
      if (hasData) completedRounds.push(r);
    }

    if (completedRounds.length === 0) return null;

    // Build cumulative scores per round for each player
    const playerLines = playerIds.map(playerId => {
      const playerData = wizardGame.playerData[playerId];
      let cumulative = 0;
      const points = [{ round: 0, score: 0 }]; // Start at 0

      completedRounds.forEach(roundNum => {
        const roundData = playerData?.rounds[roundNum];
        if (roundData?.score !== undefined) {
          cumulative += roundData.score;
        }
        points.push({ round: roundNum, score: cumulative });
      });

      return {
        playerId,
        name: playerData?.name || 'Unknown',
        points
      };
    });

    // Calculate positions per round
    const roundPositions = {};
    completedRounds.forEach(roundNum => {
      const scores = playerLines.map(p => ({
        playerId: p.playerId,
        score: p.points.find(pt => pt.round === roundNum)?.score || 0
      }));
      scores.sort((a, b) => b.score - a.score);
      scores.forEach((s, idx) => {
        if (!roundPositions[s.playerId]) roundPositions[s.playerId] = {};
        roundPositions[s.playerId][roundNum] = idx + 1;
      });
    });

    // Add positions to player lines
    playerLines.forEach(player => {
      player.positions = roundPositions[player.playerId] || {};
    });

    // Find min/max for scaling
    const allScores = playerLines.flatMap(p => p.points.map(pt => pt.score));
    const minScore = Math.min(...allScores, 0);
    const maxScore = Math.max(...allScores, 10);

    return {
      playerLines,
      completedRounds,
      minScore,
      maxScore,
      totalRounds: wizardGame.totalRounds
    };
  }, [wizardGame]);

  // Calculate forbidden bid for dealer (last player to bid)
  const forbiddenBid = useMemo(() => {
    if (!wizardGame) return null;
    const cardsThisRound = wizardGame.currentRound;
    const forbidden = cardsThisRound - totalBids;
    // Only valid if between 0 and cards this round
    if (forbidden >= 0 && forbidden <= cardsThisRound) {
      return forbidden;
    }
    return null;
  }, [wizardGame, totalBids]);

  // Check how many players have bid (to know if dealer is bidding)
  const bidsEnteredCount = useMemo(() => {
    if (!wizardGame) return 0;
    return wizardGame.playerOrder.filter(pid => bids[pid] !== '' && bids[pid] !== undefined).length;
  }, [wizardGame, bids]);

  const handleBidChange = (playerId, value) => {
    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setBids(prev => ({ ...prev, [playerId]: numValue }));
  };

  const handleTricksChange = (playerId, value) => {
    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setTricks(prev => ({ ...prev, [playerId]: numValue }));
  };

  const getBidOptions = () => {
    if (!wizardGame) return [];
    return Array.from({ length: wizardGame.currentRound + 1 }, (_, i) => i);
  };

  const handleSaveBids = async () => {
    if (!bidsAreValid) {
      setError('Total bids cannot equal the number of cards - someone must miss!');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const bidsToSave = {};
      for (const [playerId, bid] of Object.entries(bids)) {
        bidsToSave[playerId] = parseInt(bid);
      }

      await saveBidsForRound(gameId, wizardGame.currentRound, bidsToSave);
      setSuccess('Bids saved!');
      await loadData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to save bids: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTricks = async () => {
    if (!allTricksEntered) {
      setError('Please enter tricks for all players');
      return;
    }

    const tricksToSave = {};
    for (const [playerId, t] of Object.entries(tricks)) {
      tricksToSave[playerId] = parseInt(t);
    }

    const validation = validateTricks(wizardGame.currentRound, tricksToSave);
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result = await saveTricksForRound(gameId, wizardGame.currentRound, tricksToSave);

      if (result.isLastRound) {
        setSuccess('Game finished!');
        await loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Auto-advance to next round
        await startNextRound(gameId);
        setSuccess('Round ' + (wizardGame.currentRound + 1) + ' started!');
        await loadData();
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (err) {
      setError('Failed to save tricks: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleNextRound = async () => {
    setSaving(true);
    setError('');

    try {
      await startNextRound(gameId);
      setSuccess('Round ' + (wizardGame.currentRound + 1) + ' started!');
      await loadData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to start next round: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetGame = async () => {
    if (!window.confirm('Reset game? All scores will be lost.')) return;

    setSaving(true);
    setError('');

    try {
      await resetWizardGame(gameId);
      setSuccess('Game reset!');
      await loadData();
    } catch (err) {
      setError('Failed to reset: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Confirm final results and save to game
  const handleConfirmResults = async () => {
    setSaving(true);
    setError('');

    try {
      // Get final standings (already sorted by score descending)
      const standings = standingsWithStats;
      const playerCount = standings.length;

      // Calculate points: winner gets playerCount points, 2nd gets playerCount-1, etc.
      const resultsData = standings.map((player, index) => ({
        playerId: player.playerId,
        competitionId: competitionId,
        categoryId: game?.categoryId || null,
        pointsEntered: playerCount - index, // 6 for 1st, 5 for 2nd, etc.
        participated: true
      }));

      // Save results to game
      await saveGameResults(gameId, resultsData);

      // Update game status to played
      await updateGameStatus(gameId, 'played');

      setShowConfirmResults(false);
      setSuccess('Results saved! Redirecting...');

      // Navigate back to game details after short delay
      setTimeout(() => {
        navigate(`/competition/${competitionId}/game/${gameId}`);
      }, 1500);
    } catch (err) {
      setError('Failed to save results: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Edit round handlers
  const handleStartEditRound = (roundNumber, forceBidsOnly = false) => {
    const roundBids = {};
    const roundTricks = {};
    let hasTricks = false;

    wizardGame.playerOrder.forEach(playerId => {
      const roundData = wizardGame.playerData[playerId]?.rounds[roundNumber] || {};
      roundBids[playerId] = roundData.bid !== undefined ? roundData.bid : 0;
      roundTricks[playerId] = roundData.tricks !== undefined ? roundData.tricks : 0;
      // Check if any player has tricks recorded for this round
      if (roundData.tricks !== undefined) {
        hasTricks = true;
      }
    });

    setEditBids(roundBids);
    setEditTricks(roundTricks);
    setEditingRound(roundNumber);
    // If forced bids only OR round has no tricks yet, only edit bids
    setEditBidsOnly(forceBidsOnly || !hasTricks);
  };

  const handleCancelEdit = () => {
    setEditingRound(null);
    setEditBids({});
    setEditTricks({});
    setEditBidsOnly(false);
  };

  const handleSaveEdit = async () => {
    // If editing bids only (current round), just save bids
    if (editBidsOnly) {
      setSaving(true);
      setError('');

      try {
        const bidsToSave = {};
        for (const playerId of wizardGame.playerOrder) {
          bidsToSave[playerId] = parseInt(editBids[playerId]) || 0;
        }
        await saveBidsForRound(gameId, editingRound, bidsToSave);

        setSuccess(`Round ${editingRound} bids updated!`);
        setEditingRound(null);
        setEditBids({});
        setEditTricks({});
        setEditBidsOnly(false);
        await loadData();
        setTimeout(() => setSuccess(''), 2000);
      } catch (err) {
        setError('Failed to update bids: ' + err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Full round edit - validate tricks total
    const totalEditTricks = Object.values(editTricks).reduce((sum, t) => sum + (parseInt(t) || 0), 0);
    if (totalEditTricks !== editingRound) {
      setError(`Total tricks (${totalEditTricks}) must equal cards in round (${editingRound})`);
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Update each player's round data
      for (const playerId of wizardGame.playerOrder) {
        const bid = parseInt(editBids[playerId]) || 0;
        const tricks = parseInt(editTricks[playerId]) || 0;
        await editRoundData(gameId, editingRound, playerId, bid, tricks);
      }

      setSuccess(`Round ${editingRound} updated!`);
      setEditingRound(null);
      setEditBids({});
      setEditTricks({});
      setEditBidsOnly(false);
      await loadData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to update round: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getPreviewScore = (playerId) => {
    const bid = parseInt(bids[playerId]);
    const trick = parseInt(tricks[playerId]);
    if (isNaN(bid) || isNaN(trick)) return null;
    return calculateRoundScore(bid, trick);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-purple-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mx-auto"></div>
          <p className="mt-3 text-purple-200 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!wizardGame) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HamburgerMenu />
        <div className="max-w-md mx-auto px-4 py-8">
          <Card>
            <p className="text-center text-gray-600 text-sm">
              Unable to load Wizard game. Make sure draw order is set.
            </p>
            <Button
              onClick={() => navigate(`/competition/${competitionId}/game/${gameId}`)}
              className="mt-4 w-full"
              size="sm"
            >
              Back to Game
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const isFinished = wizardGame.status === 'finished';
  const isBiddingPhase = wizardGame.status === 'bidding';
  const isPlayingPhase = wizardGame.status === 'playing';
  const isRoundComplete = wizardGame.status === 'round_complete';
  const cardsThisRound = wizardGame.currentRound;
  const bidOptions = getBidOptions();
  const playerCount = wizardGame.playerOrder.length;

  // Bid status colors
  const getBidStatusColor = () => {
    if (!allBidsEntered) return 'bg-gray-100 text-gray-600';
    if (totalBids < cardsThisRound) return 'bg-red-100 text-red-700'; // Underbid
    if (totalBids > cardsThisRound) return 'bg-green-100 text-green-700'; // Overbid
    return 'bg-yellow-100 text-yellow-700'; // Equal - not allowed
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900">
      <HamburgerMenu />

      {/* Compact Header */}
      <div className="bg-purple-800 px-3 py-2 md:px-6 md:py-4">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-xl md:text-4xl">🧙</span>
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-white">Wizard</h1>
              <p className="text-purple-200 text-xs md:text-sm">{game?.name}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-white font-bold text-base md:text-xl">
              R{wizardGame.currentRound}/{wizardGame.totalRounds}
            </div>
            <div className="text-purple-200 text-xs md:text-sm">
              {cardsThisRound} card{cardsThisRound !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 py-3 md:px-6 md:py-4 space-y-3 md:space-y-4 max-w-4xl mx-auto">
        {/* Messages */}
        {error && (
          <div className="p-2 md:p-3 bg-red-100 border border-red-300 rounded text-red-800 text-xs md:text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-2 md:p-3 bg-green-100 border border-green-300 rounded text-green-800 text-xs md:text-sm">
            {success}
          </div>
        )}

        {/* Last round notification */}
        {!isFinished && wizardGame.currentRound === wizardGame.totalRounds && (
          <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-lg px-3 py-2 md:px-4 md:py-3 flex items-center gap-2 md:gap-3 shadow-lg animate-pulse">
            <span className="text-lg md:text-2xl">🏁</span>
            <span className="text-white text-sm md:text-lg font-bold">
              FINAL ROUND!
            </span>
          </div>
        )}

        {/* Dealer - compact with card count */}
        {currentDealer && !isFinished && (
          <div className="bg-yellow-100 border border-yellow-400 rounded px-3 py-2 md:px-4 md:py-3 flex items-center gap-2 md:gap-3">
            <span className="text-lg md:text-2xl">🎴</span>
            <span className="text-yellow-800 text-sm md:text-base font-medium flex-1">
              Dealer: {wizardGame.playerData[currentDealer.playerId]?.name}
            </span>
            <span className="text-yellow-700 text-sm md:text-base font-bold bg-yellow-200 px-2 py-0.5 rounded">
              {cardsThisRound} card{cardsThisRound !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Game finished */}
        {isFinished && !showConfirmResults && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-3 md:p-5 text-center">
            <span className="text-3xl md:text-5xl">🏆</span>
            <h2 className="text-lg md:text-2xl font-bold text-white">Game Complete!</h2>
            <p className="text-white/90 text-sm md:text-lg">
              {standingsWithStats[0]?.name}: {standingsWithStats[0]?.totalScore} pts
            </p>
            {isOrganizer && (
              <button
                onClick={() => setShowConfirmResults(true)}
                className="mt-3 px-4 py-2 bg-white text-orange-600 rounded-lg font-bold text-sm md:text-base hover:bg-orange-50"
              >
                Confirm & Save Results
              </button>
            )}
          </div>
        )}

        {/* Results Confirmation Dialog */}
        {isFinished && showConfirmResults && (
          <Card className="bg-white p-4 md:p-6 border-2 border-green-400">
            <div className="text-center mb-4">
              <span className="text-4xl md:text-5xl">🏆</span>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mt-2">Confirm Final Results?</h2>
              <p className="text-sm text-gray-600 mt-1">
                This will save scores to the game and mark it as completed.
              </p>
            </div>

            {/* Show final standings with points to be awarded */}
            <div className="space-y-2 mb-4">
              {standingsWithStats.map((player, index) => {
                const pointsAwarded = standingsWithStats.length - index;
                return (
                  <div
                    key={player.playerId}
                    className={`flex items-center justify-between p-2 rounded ${
                      index === 0 ? 'bg-yellow-100 border border-yellow-400' :
                      index === 1 ? 'bg-gray-100 border border-gray-300' :
                      index === 2 ? 'bg-orange-50 border border-orange-300' :
                      'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-600 text-white' :
                        'bg-gray-300 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                      <span className="font-medium text-sm">{player.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-bold ${
                        player.totalScore >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {player.totalScore} pts
                      </span>
                      <span className="text-sm font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                        +{pointsAwarded} score
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setShowConfirmResults(false)}
                variant="outline"
                className="flex-1"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmResults}
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700"
                size="sm"
              >
                {saving ? 'Saving...' : 'Confirm Results'}
              </Button>
            </div>
          </Card>
        )}

        {/* Standings - horizontal row layout */}
        <Card className="bg-white/95 p-3 md:p-4">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <h2 className="text-sm md:text-lg font-bold text-gray-900">Standings</h2>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-xs md:text-sm text-purple-600"
            >
              {showHistory ? 'Hide' : 'History'}
            </button>
          </div>

          {/* Row 1: Player names with position */}
          <div className="flex gap-1 md:gap-2 mb-1">
            {standingsWithStats.map((player, index) => (
              <div
                key={player.playerId}
                className={`flex-1 text-center p-1 md:p-2 rounded-t ${
                  index === 0 && isFinished ? 'bg-yellow-100' : 'bg-gray-100'
                }`}
              >
                <div className={`w-5 h-5 md:w-7 md:h-7 mx-auto rounded-full flex items-center justify-center font-bold text-xs md:text-sm mb-1 ${
                  index === 0 ? 'bg-yellow-500 text-white' :
                  index === 1 ? 'bg-gray-400 text-white' :
                  index === 2 ? 'bg-orange-600 text-white' :
                  'bg-gray-300 text-gray-600'
                }`}>
                  {index + 1}
                </div>
                <p className="font-medium text-gray-900 text-xs md:text-sm truncate">{player.name}</p>
              </div>
            ))}
          </div>

          {/* Row 2: Scores */}
          <div className="flex gap-1 md:gap-2 mb-1">
            {standingsWithStats.map((player, index) => (
              <div
                key={player.playerId}
                className={`flex-1 text-center py-1 md:py-2 ${
                  index === 0 && isFinished ? 'bg-yellow-50' : 'bg-gray-50'
                }`}
              >
                <span className={`font-bold text-lg md:text-2xl ${
                  player.totalScore >= 0 ? 'text-green-700' : 'text-red-600'
                }`}>
                  {player.totalScore}
                </span>
              </div>
            ))}
          </div>

          {/* Row 3: Made/Busted stats */}
          <div className="flex gap-1 md:gap-2">
            {standingsWithStats.map((player, index) => (
              <div
                key={player.playerId}
                className={`flex-1 text-center py-1 md:py-2 rounded-b ${
                  index === 0 && isFinished ? 'bg-yellow-50' : 'bg-gray-50'
                }`}
              >
                <span className="text-xs md:text-sm">
                  <span className="text-green-600">{player.made}✓</span>
                  <span className="mx-1 text-gray-400">/</span>
                  <span className="text-red-600">{player.busted}✗</span>
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Round History - Grid layout with edit buttons */}
        {showHistory && (
          <Card className="bg-white/95 p-3 md:p-4">
            <h2 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3">History</h2>

            {/* Round edit buttons for organizers */}
            {isOrganizer && !isFinished && (
              <div className="flex flex-wrap gap-1 md:gap-2 mb-3">
                <span className="text-xs md:text-sm text-gray-500 mr-1">Edit Round:</span>
                {Array.from({ length: wizardGame.currentRound }, (_, i) => {
                  const roundNum = i + 1;
                  const hasData = playersInOrder.some(p =>
                    wizardGame.playerData[p.id]?.rounds[roundNum]?.score !== undefined
                  );
                  if (!hasData) return null;
                  return (
                    <button
                      key={roundNum}
                      onClick={() => handleStartEditRound(roundNum)}
                      className="w-7 h-7 md:w-9 md:h-9 rounded bg-orange-100 text-orange-700 text-xs md:text-sm font-bold hover:bg-orange-200"
                    >
                      {roundNum}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="space-y-2 md:space-y-3">
              {playersInOrder.map(player => {
                const playerData = wizardGame.playerData[player.id];
                return (
                  <div key={player.id} className="bg-gray-50 rounded p-2 md:p-3">
                    <div className="text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2 truncate">
                      {player.displayName}
                    </div>
                    <div className="flex flex-wrap gap-1 md:gap-2">
                      {Array.from({ length: wizardGame.currentRound }, (_, i) => {
                        const round = playerData?.rounds[i + 1];
                        if (!round || round.score === undefined) {
                          return (
                            <div key={i + 1} className="w-8 h-8 md:w-10 md:h-10 rounded bg-gray-200 flex items-center justify-center text-xs md:text-sm text-gray-400">
                              -
                            </div>
                          );
                        }
                        const made = round.bid === round.tricks;
                        return (
                          <div
                            key={i + 1}
                            className={`w-8 h-8 md:w-10 md:h-10 rounded flex flex-col items-center justify-center text-xs md:text-sm font-bold ${
                              made ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}
                            title={`R${i + 1}: Bid ${round.bid}, Got ${round.tricks}`}
                          >
                            <span className="text-[10px] md:text-xs leading-none">{round.bid}/{round.tricks}</span>
                            <span className="leading-none">{round.score > 0 ? '+' : ''}{round.score}</span>
                          </div>
                        );
                      })}
                      <div className={`w-10 h-8 md:w-12 md:h-10 rounded flex items-center justify-center text-xs md:text-sm font-bold ${
                        playerData?.totalScore >= 0 ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                      }`}>
                        {playerData?.totalScore || 0}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Score Graph */}
            {graphData && graphData.completedRounds.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-xs md:text-sm font-bold text-gray-700 mb-3">Score Progress</h3>

                {/* SVG Line Chart - Full Width */}
                <div className="bg-gray-50 rounded p-2 md:p-4">
                  <svg
                    viewBox="0 0 400 160"
                    className="w-full h-auto"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    {/* Grid lines */}
                    <line x1="30" y1="15" x2="30" y2="125" stroke="#e5e7eb" strokeWidth="1" />
                    <line x1="30" y1="125" x2="390" y2="125" stroke="#e5e7eb" strokeWidth="1" />
                    <line x1="30" y1="70" x2="390" y2="70" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="4" />
                    <line x1="30" y1="15" x2="390" y2="15" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="4" />

                    {/* Y-axis labels */}
                    <text x="26" y="20" textAnchor="end" fontSize="9" fill="#6b7280">{graphData.maxScore}</text>
                    <text x="26" y="73" textAnchor="end" fontSize="9" fill="#6b7280">
                      {Math.round((graphData.maxScore + graphData.minScore) / 2)}
                    </text>
                    <text x="26" y="128" textAnchor="end" fontSize="9" fill="#6b7280">{graphData.minScore}</text>

                    {/* X-axis labels (rounds) */}
                    {graphData.completedRounds.map((roundNum, idx) => {
                      const x = 30 + ((idx + 1) / (graphData.completedRounds.length)) * 360;
                      return (
                        <text key={roundNum} x={x} y="142" textAnchor="middle" fontSize="9" fill="#6b7280">
                          R{roundNum}
                        </text>
                      );
                    })}

                    {/* Player lines */}
                    {graphData.playerLines.map((player, playerIdx) => {
                      const colors = ['#8b5cf6', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#06b6d4'];
                      const color = colors[playerIdx % colors.length];
                      const scoreRange = graphData.maxScore - graphData.minScore || 1;

                      // Build path - use full width (360px chart area)
                      const pathPoints = player.points.map((pt, idx) => {
                        const x = 30 + (idx / graphData.completedRounds.length) * 360;
                        const y = 125 - ((pt.score - graphData.minScore) / scoreRange) * 110;
                        return `${idx === 0 ? 'M' : 'L'} ${x} ${y}`;
                      }).join(' ');

                      return (
                        <g key={player.playerId}>
                          <path
                            d={pathPoints}
                            fill="none"
                            stroke={color}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          {/* Points */}
                          {player.points.slice(1).map((pt, idx) => {
                            const x = 30 + ((idx + 1) / graphData.completedRounds.length) * 360;
                            const y = 125 - ((pt.score - graphData.minScore) / scoreRange) * 110;
                            return (
                              <circle
                                key={idx}
                                cx={x}
                                cy={y}
                                r="4"
                                fill={color}
                              />
                            );
                          })}
                        </g>
                      );
                    })}
                  </svg>

                  {/* Legend */}
                  <div className="flex flex-wrap gap-2 mt-2 justify-center">
                    {graphData.playerLines.map((player, idx) => {
                      const colors = ['#8b5cf6', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#06b6d4'];
                      const color = colors[idx % colors.length];
                      const lastRound = graphData.completedRounds[graphData.completedRounds.length - 1];
                      const position = player.positions[lastRound] || '-';
                      return (
                        <div key={player.playerId} className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                          <span className="text-xs text-gray-600">{player.name}</span>
                          <span className="text-xs font-bold text-gray-400">#{position}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Position Table */}
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left py-1 px-1">Player</th>
                        {graphData.completedRounds.map(r => (
                          <th key={r} className="text-center py-1 px-1">R{r}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {graphData.playerLines.map((player, idx) => {
                        const colors = ['bg-purple-100', 'bg-green-100', 'bg-blue-100', 'bg-yellow-100', 'bg-red-100', 'bg-cyan-100'];
                        const bgColor = colors[idx % colors.length];
                        return (
                          <tr key={player.playerId} className={bgColor}>
                            <td className="py-1 px-1 font-medium truncate max-w-[80px]">{player.name}</td>
                            {graphData.completedRounds.map(r => {
                              const pos = player.positions[r];
                              return (
                                <td key={r} className="text-center py-1 px-1">
                                  <span className={`inline-block w-5 h-5 rounded-full text-xs font-bold leading-5 ${
                                    pos === 1 ? 'bg-yellow-500 text-white' :
                                    pos === 2 ? 'bg-gray-400 text-white' :
                                    pos === 3 ? 'bg-orange-500 text-white' :
                                    'bg-gray-200 text-gray-600'
                                  }`}>
                                    {pos}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Edit Round Modal */}
        {editingRound && (
          <Card className={`bg-white p-3 md:p-4 border-2 ${editBidsOnly ? 'border-purple-400' : 'border-orange-400'}`}>
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h2 className={`text-sm md:text-lg font-bold ${editBidsOnly ? 'text-purple-800' : 'text-orange-800'}`}>
                {editBidsOnly ? `Edit Round ${editingRound} Bids` : `Edit Round ${editingRound}`} ({editingRound} card{editingRound !== 1 ? 's' : ''})
              </h2>
              <button
                onClick={handleCancelEdit}
                className="text-gray-500 hover:text-gray-700 text-lg md:text-xl"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2 md:space-y-3">
              {playersInOrder.map(player => {
                const editBidOptions = Array.from({ length: editingRound + 1 }, (_, i) => i);
                return (
                  <div key={player.id} className="p-2 md:p-3 bg-gray-50 rounded">
                    <div className="text-xs md:text-sm font-medium text-gray-700 mb-2">{player.displayName}</div>
                    {/* Bids row */}
                    <div className="mb-2">
                      <div className="text-xs md:text-sm text-gray-500 mb-1">Bid</div>
                      <div className="flex flex-wrap gap-1 md:gap-2">
                        {editBidOptions.map(n => (
                          <button
                            key={n}
                            onClick={() => setEditBids(prev => ({ ...prev, [player.id]: n }))}
                            className={`w-7 h-7 md:w-10 md:h-10 rounded text-xs md:text-base font-bold ${
                              parseInt(editBids[player.id]) === n
                                ? 'bg-purple-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700'
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Tricks row - only shown when editing full round */}
                    {!editBidsOnly && (
                      <div>
                        <div className="text-xs md:text-sm text-gray-500 mb-1">Tricks</div>
                        <div className="flex flex-wrap gap-1 md:gap-2">
                          {editBidOptions.map(n => (
                            <button
                              key={n}
                              onClick={() => setEditTricks(prev => ({ ...prev, [player.id]: n }))}
                              className={`w-7 h-7 md:w-10 md:h-10 rounded text-xs md:text-base font-bold ${
                                parseInt(editTricks[player.id]) === n
                                  ? 'bg-green-600 text-white'
                                  : 'bg-white border border-gray-300 text-gray-700'
                              }`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Edit total indicator - only show for full round edit */}
            {!editBidsOnly && (
              <div className={`mt-2 md:mt-3 p-2 md:p-3 rounded text-center text-xs md:text-sm font-bold ${
                Object.values(editTricks).reduce((sum, t) => sum + (parseInt(t) || 0), 0) === editingRound
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                Total Tricks: {Object.values(editTricks).reduce((sum, t) => sum + (parseInt(t) || 0), 0)} / {editingRound}
              </div>
            )}

            <div className="mt-3 md:mt-4 flex gap-2 md:gap-3">
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="flex-1"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving || (!editBidsOnly && Object.values(editTricks).reduce((sum, t) => sum + (parseInt(t) || 0), 0) !== editingRound)}
                className={`flex-1 ${editBidsOnly ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                size="sm"
              >
                {saving ? 'Saving...' : editBidsOnly ? 'Save Bids' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        )}

        {/* Quick Edit Buttons */}
        {!isFinished && isOrganizer && (
          <div className="flex gap-2 flex-wrap">
            {/* Edit last completed round */}
            {wizardGame.currentRound > 1 && (
              <button
                onClick={() => handleStartEditRound(
                  isPlayingPhase || isBiddingPhase ? wizardGame.currentRound - 1 : wizardGame.currentRound
                )}
                className="flex-1 px-3 py-2 bg-orange-100 border border-orange-300 rounded-lg text-orange-700 text-sm font-medium hover:bg-orange-200 flex items-center justify-center gap-2"
              >
                <span>✏️</span>
                <span>Edit Round {isPlayingPhase || isBiddingPhase ? wizardGame.currentRound - 1 : wizardGame.currentRound}</span>
              </button>
            )}
            {/* Edit current round bids (only when bids are entered but tricks phase) */}
            {isPlayingPhase && (
              <button
                onClick={() => handleStartEditRound(wizardGame.currentRound, true)}
                className="flex-1 px-3 py-2 bg-purple-100 border border-purple-300 rounded-lg text-purple-700 text-sm font-medium hover:bg-purple-200 flex items-center justify-center gap-2"
              >
                <span>✏️</span>
                <span>Edit R{wizardGame.currentRound} Bids</span>
              </button>
            )}
          </div>
        )}

        {/* Entry Form */}
        {!isFinished && isOrganizer && (
          <Card className="bg-white/95 p-3 md:p-4">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <h2 className="text-sm md:text-lg font-bold text-gray-900">
                {isBiddingPhase ? 'Enter Bids' : 'Enter Tricks'}
              </h2>

              {/* Bid/Trick total indicator - more visible */}
              {isBiddingPhase && (
                <div className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-lg font-bold shadow-sm border-2 ${
                  !allBidsEntered ? 'bg-gray-100 text-gray-600 border-gray-300' :
                  totalBids < cardsThisRound ? 'bg-red-100 text-red-700 border-red-400' :
                  totalBids > cardsThisRound ? 'bg-green-100 text-green-700 border-green-400' :
                  'bg-yellow-100 text-yellow-700 border-yellow-400'
                }`}>
                  <span className="text-base md:text-xl">{totalBids}</span>
                  <span className="mx-1">/</span>
                  <span className="text-base md:text-xl">{cardsThisRound}</span>
                  {totalBids < cardsThisRound && ' ↓'}
                  {totalBids > cardsThisRound && ' ↑'}
                  {totalBids === cardsThisRound && allBidsEntered && ' ⚠️'}
                </div>
              )}
              {isPlayingPhase && (
                <div className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm md:text-lg font-bold shadow-sm border-2 ${
                  totalTricks === cardsThisRound ? 'bg-green-100 text-green-700 border-green-400' : 'bg-red-100 text-red-700 border-red-400'
                }`}>
                  <span className="text-base md:text-xl">{totalTricks}</span>
                  <span className="mx-1">/</span>
                  <span className="text-base md:text-xl">{cardsThisRound}</span>
                </div>
              )}
            </div>

            {/* Grid layout for players - dealer bids last - ultra compact */}
            <div className="space-y-1">
              {playersInBiddingOrder.map((player, playerIndex) => {
                const isDealer = currentDealer?.playerId === player.id;
                const previewScore = isPlayingPhase ? getPreviewScore(player.id) : null;
                const playerBid = parseInt(bids[player.id]);
                const playerTricks = parseInt(tricks[player.id]);
                const showForbiddenWarning = isBiddingPhase && isDealer && bidsEnteredCount === playerCount - 1 && forbiddenBid !== null;
                // Get current ranking (1-based position in standings)
                const currentRank = standingsWithStats.findIndex(s => s.playerId === player.id) + 1;

                return (
                  <div
                    key={player.id}
                    className={`px-1.5 py-1 md:px-2 md:py-1.5 rounded border ${
                      isDealer ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    {/* Player info row - inline with bid buttons */}
                    <div className="flex items-center gap-1.5 md:gap-2 mb-1">
                      <span className="text-xs text-gray-500">#{player.seatNumber}</span>
                      <span className="font-medium text-sm text-gray-900 flex-1 truncate">
                        {player.displayName}
                        {isDealer && <span className="ml-1">🎴</span>}
                      </span>
                      {/* Score and Ranking - compact inline */}
                      <span className={`text-xs md:text-sm font-bold ${
                        player.totalScore >= 0 ? 'text-green-700' : 'text-red-600'
                      }`}>
                        {player.totalScore}pts
                      </span>
                      <span className="text-xs text-gray-400">#{currentRank}</span>
                      {previewScore !== null && (
                        <span className={`text-sm font-bold ${
                          previewScore >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {previewScore > 0 ? '+' : ''}{previewScore}
                        </span>
                      )}
                    </div>

                    {/* Show forbidden bid warning for dealer */}
                    {showForbiddenWarning && (
                      <div className="mb-1 px-2 py-0.5 bg-red-100 border border-red-300 rounded text-xs text-red-700">
                        Cannot bid <span className="font-bold">{forbiddenBid}</span>
                      </div>
                    )}

                    {/* Bid/Trick buttons - compact grid */}
                    <div className="flex gap-1.5 md:gap-2">
                      {/* Bid buttons or bid display */}
                      <div className={`${(isPlayingPhase || isRoundComplete) ? 'w-14 md:w-16' : 'flex-1'}`}>
                        <div className="text-xs text-gray-500 mb-0.5">Bid</div>
                        {(isPlayingPhase || isRoundComplete) ? (
                          // Show highlighted bid during tricks entry
                          <div className="w-10 h-8 md:w-12 md:h-10 rounded bg-purple-600 text-white font-bold text-lg md:text-xl flex items-center justify-center shadow-sm border-2 border-purple-400">
                            {playerBid}
                          </div>
                        ) : (
                          // Bid entry buttons
                          <div className="flex flex-wrap gap-1">
                            {bidOptions.map(n => {
                              const isForbidden = isDealer && bidsEnteredCount === playerCount - 1 && n === forbiddenBid;
                              return (
                                <button
                                  key={n}
                                  onClick={() => handleBidChange(player.id, n)}
                                  disabled={isForbidden}
                                  className={`w-7 h-7 md:w-8 md:h-8 rounded font-bold text-xs md:text-sm ${
                                    playerBid === n
                                      ? 'bg-purple-600 text-white'
                                      : isForbidden
                                      ? 'bg-red-200 text-red-400 line-through'
                                      : 'bg-white border border-gray-300 text-gray-700'
                                  }`}
                                >
                                  {n}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Tricks buttons */}
                      {(isPlayingPhase || isRoundComplete) && (
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 mb-0.5">Tricks</div>
                          <div className="flex flex-wrap gap-1">
                            {bidOptions.map(n => {
                              const isSelected = playerTricks === n;
                              const isBusted = isSelected && !isNaN(playerBid) && n !== playerBid;
                              return (
                                <button
                                  key={n}
                                  onClick={() => handleTricksChange(player.id, n)}
                                  disabled={isRoundComplete}
                                  className={`w-7 h-7 md:w-8 md:h-8 rounded font-bold text-xs md:text-sm ${
                                    isSelected
                                      ? isBusted
                                        ? 'bg-red-600 text-white'
                                        : 'bg-green-600 text-white'
                                      : 'bg-white border border-gray-300 text-gray-700'
                                  } ${isRoundComplete ? 'opacity-40' : ''}`}
                                >
                                  {n}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bid validation message */}
            {isBiddingPhase && allBidsEntered && totalBids === cardsThisRound && (
              <div className="mt-2 md:mt-3 p-2 md:p-3 bg-yellow-100 border border-yellow-400 rounded text-xs md:text-sm text-yellow-800">
                Total bids ({totalBids}) cannot equal cards ({cardsThisRound}). Dealer must change their bid!
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-3 md:mt-4 flex gap-2 md:gap-3">
              {isBiddingPhase && (
                <Button
                  onClick={handleSaveBids}
                  disabled={saving || !bidsAreValid}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  size="sm"
                >
                  {saving ? 'Saving...' : 'Confirm Bids'}
                </Button>
              )}

              {isPlayingPhase && (
                <Button
                  onClick={handleSaveTricks}
                  disabled={saving || !allTricksEntered || totalTricks !== cardsThisRound}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  {saving ? 'Saving...' : 'Save Results'}
                </Button>
              )}

              {isRoundComplete && wizardGame.currentRound < wizardGame.totalRounds && (
                <Button
                  onClick={handleNextRound}
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  {saving ? 'Starting...' : `Next Round (${wizardGame.currentRound + 1})`}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Bottom actions */}
        <div className="flex gap-2 md:gap-3">
          <Button
            onClick={() => navigate(`/competition/${competitionId}/game/${gameId}`)}
            variant="outline"
            className="flex-1 bg-white/90"
            size="sm"
          >
            Back
          </Button>

          {isOrganizer && (
            <Button
              onClick={handleResetGame}
              variant="outline"
              className="bg-white/90 text-red-600 border-red-300"
              size="sm"
            >
              Reset
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WizardScoreboard;
